"use client";
import { CategoryNode } from "@/types/pim";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useInventoryNav } from "./InventoryNavContext";
import { useInventoryFilters } from "./InventoryFiltersContext";

interface DesktopToolbarProps {
  selectedCategory: CategoryNode | null;
}

const Subcategories: React.FC<DesktopToolbarProps> = ({ selectedCategory }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { navigate } = useInventoryNav();

  // Recalculated child counts for the current filters/search come from the server.
  // null = no active filters → fall back to the static itemCount on each child.
  const { subcategoryCounts } = useInventoryFilters();

  const getCount = (subcat: CategoryNode): number =>
    subcategoryCounts ? (subcategoryCounts[subcat.Id] ?? 0) : (subcat.itemCount ?? 0);

  const getSubcategoryLink = (subcat: CategoryNode) => {
    const base = pathname.startsWith('/inventory/all')
      // On the All view the chips are root categories — strip the "/all" segment so
      // they link to the normal category route (e.g. /inventory/forklifts).
      ? `${pathname.replace('/all', '')}/${subcat.Slug}`
      : `${pathname}/${subcat.Slug}`;

    // Carry the Location and tag filters across category/subcategory navigation.
    const locationValues = searchParams.getAll('Location');
    const tagValues = searchParams.getAll('tags');
    if (locationValues.length === 0 && tagValues.length === 0) {
      return base;
    }

    const params = new URLSearchParams();
    locationValues.forEach((value) => params.append('Location', value));
    tagValues.forEach((value) => params.append('tags', value));
    return `${base}?${params.toString()}`;
  };

  const visibleSubcategories = selectedCategory?.children?.length
    ? [...selectedCategory.children]
        .filter((subcat) => getCount(subcat) > 0)
        .sort((a, b) => a.Name.localeCompare(b.Name))
    : [];

  if (visibleSubcategories.length === 0) {
    return null;
  }

  return (
    <div className="bg-white">
      <div className="flex flex-col">
        <div className="flex items-center gap-2 flex-wrap px-3 py-1.5">
          {visibleSubcategories.map((subcat) => {
            const href = getSubcategoryLink(subcat);
            return (
              <Link
                key={subcat.Id}
                href={href}
                scroll={false}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
                    return;
                  }
                  e.preventDefault();
                  navigate(href);
                }}
                className="shrink-0 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-100 active:bg-gray-200 transition-colors whitespace-nowrap"
              >
                <span>{subcat.Name} ({getCount(subcat)})</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Subcategories;
